import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  BookOpen,
  FileText,
  MessageSquare,
  History,
  Users,
  Scroll,
  Sparkles,
  Loader2,
  ArrowRight,
  X,
} from 'lucide-react';
import { searchApi, tafsirApi } from '../services/api';
import { Input } from './ui/input';

const CATEGORY_META = {
  surahs:     { label: 'Sourates',   icon: BookOpen,      testid: 'surahs' },
  verses:     { label: 'Versets',    icon: FileText,      testid: 'verses' },
  hadiths:    { label: 'Hadiths',    icon: MessageSquare, testid: 'hadiths' },
  tafsir:     { label: 'Tafsir',     icon: Sparkles,      testid: 'tafsir' },
  events:     { label: 'Événements', icon: History,       testid: 'events' },
  companions: { label: 'Compagnons', icon: Users,         testid: 'companions' },
  prophets:   { label: 'Prophètes',  icon: Scroll,        testid: 'prophets' },
};

const ORDER = ['surahs', 'verses', 'tafsir', 'hadiths', 'events', 'companions', 'prophets'];
const PREVIEW_THRESHOLD = 3;
const MIN_CHARS = 2;

const useDebounced = (value, delay = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

const getItemRoute = (cat, item) => {
  switch (cat) {
    case 'surahs':     return `/quran/${item.number}`;
    case 'verses':     return `/quran/${item.surah_number}`;
    case 'tafsir':     return `/quran/${item.surah_number}`;
    case 'hadiths':    return `/hadith/entry/${item.id}`;
    case 'events':     return `/events/${item.id}`;
    case 'companions': return `/companions/${item.id}`;
    case 'prophets':   return `/prophets/${item.id}`;
    default: return '/';
  }
};

const getItemLabel = (cat, item) => {
  switch (cat) {
    case 'surahs':     return item.name_french || item.name_english;
    case 'verses':     return item.translation_french || item.translation_english || item.arabic_text;
    case 'tafsir':     return item.snippet;
    case 'hadiths':    return item.preview_en || item.preview_ar || item.narrator_english;
    case 'events':     return item.title_french || item.title_english;
    case 'companions': return item.name_french || item.name_english;
    case 'prophets':   return item.name_french || item.name_english;
    default: return '';
  }
};

const getItemSubtitle = (cat, item) => {
  switch (cat) {
    case 'surahs':     return `Sourate ${item.number} · ${item.verses_count} versets`;
    case 'verses':     return `${item.surah_number}:${item.verse_number}`;
    case 'tafsir':     return `${item.source?.display_name_localized?.fr || item.source?.display_name || ''} · ${item.surah_number}:${item.verse_number}`;
    case 'hadiths':    return item.collection_slug ? `${item.collection_slug === 'bukhari' ? 'Boukhari' : 'Muslim'} · Livre ${item.book_number} · #${item.id_in_book}` : null;
    case 'events':     return item.date_gregorian || item.date_hijri;
    case 'companions': return item.nickname || item.relationship_to_prophet;
    case 'prophets':   return item.era;
    default: return null;
  }
};

export const GlobalSearchBar = ({
  variant = 'navbar',
  placeholder,
  autoFocus = false,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const ph = placeholder || t('nav.search') + '...';
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const debouncedQuery = useDebounced(query, 250);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch suggestions
  useEffect(() => {
    const q = debouncedQuery.trim();
    if (q.length < MIN_CHARS) {
      setResults(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      searchApi.search(q, null, 10, 0),
      tafsirApi.search(q, { limit: 3, skip: 0 }).catch(() => ({ data: { total: 0, items: [] } })),
    ])
      .then(([mainRes, tafRes]) => {
        if (!cancelled) {
          // Merge tafsir into the same shape used by the dropdown
          const tafsirItems = (tafRes.data?.items || []).map((t) => ({
            // Re-shape into something getItemLabel/Route can consume
            id: t.group_id,
            group_id: t.group_id,
            surah_number: t.group.from_surah,
            verse_number: t.group.from_ayah,
            snippet: t.snippet,
            source: t.source,
            language_code: t.language_code,
          }));
          const merged = {
            ...mainRes.data,
            tafsir: tafsirItems,
            counts: {
              ...mainRes.data.counts,
              tafsir: tafRes.data?.total || 0,
            },
          };
          setResults(merged);
          setOpen(true);
        }
      })
      .catch(() => {
        if (!cancelled) setResults(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const handleSubmit = useCallback(
    (e) => {
      e?.preventDefault();
      const q = query.trim();
      if (!q) return;
      setOpen(false);
      navigate(`/search?q=${encodeURIComponent(q)}`);
      onNavigate?.();
    },
    [query, navigate, onNavigate],
  );

  const goToCategory = useCallback(
    (cat) => {
      const q = query.trim();
      if (!q) return;
      setOpen(false);
      navigate(`/search?q=${encodeURIComponent(q)}&category=${cat}`);
      onNavigate?.();
    },
    [query, navigate, onNavigate],
  );

  const goToItem = useCallback(
    (cat, item) => {
      setOpen(false);
      const route = getItemRoute(cat, { ...item, _query: query });
      navigate(route);
      onNavigate?.();
    },
    [navigate, query, onNavigate],
  );

  const totalHits =
    results?.counts
      ? Object.values(results.counts).reduce((a, b) => a + b, 0)
      : 0;

  const hasAnyResults = totalHits > 0;
  const showDropdown =
    open && debouncedQuery.trim().length >= MIN_CHARS && (loading || results);

  const widthClass =
    variant === 'hero' ? 'max-w-2xl' : 'max-w-sm lg:max-w-md';

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${widthClass}`}
      data-testid="global-search-container"
    >
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
              loading ? 'text-primary' : 'text-text-muted'
            }`}
          />
          <Input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              if (debouncedQuery.trim().length >= MIN_CHARS) setOpen(true);
            }}
            placeholder={ph}
            autoFocus={autoFocus}
            className={`pl-9 pr-9 bg-surface/70 border-border text-sm ${
              variant === 'hero' ? 'py-6 text-base' : ''
            }`}
            data-testid="global-search-input"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-primary" />
          )}
          {!loading && query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setResults(null);
                setOpen(false);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              aria-label="Effacer"
              data-testid="global-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      {/* Autocomplete dropdown */}
      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 z-50 rounded-xl border border-border bg-background/95 backdrop-blur-xl shadow-2xl overflow-hidden"
            data-testid="global-search-dropdown"
          >
            {loading && !results && (
              <div className="px-4 py-6 text-sm text-text-muted text-center">
                Recherche en cours...
              </div>
            )}

            {!loading && results && !hasAnyResults && (
              <div className="px-4 py-6 text-sm text-text-muted text-center" data-testid="global-search-no-results">
                Aucun résultat pour « {debouncedQuery.trim()} »
              </div>
            )}

            {!!hasAnyResults && (
              <div className="max-h-[70vh] overflow-y-auto">
                {ORDER.map((cat) => {
                  const total = results?.counts?.[cat] || 0;
                  const items = results?.[cat] || [];
                  if (total === 0) return null;
                  const meta = CATEGORY_META[cat];
                  const Icon = meta.icon;
                  const showItems = total <= PREVIEW_THRESHOLD;

                  return (
                    <div
                      key={cat}
                      className="border-b border-border/50 last:border-b-0"
                      data-testid={`global-search-group-${meta.testid}`}
                    >
                      <div className="flex items-center justify-between px-3 py-2 bg-surface/50">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
                          <Icon className="w-3.5 h-3.5 text-primary" />
                          {meta.label}
                        </div>
                        <span className="text-xs text-text-muted">
                          {total} résultat{total > 1 ? 's' : ''}
                        </span>
                      </div>

                      {showItems ? (
                        <ul>
                          {items.slice(0, PREVIEW_THRESHOLD).map((item, idx) => (
                            <li key={`${cat}-${item.id || item.number || item.verse_key || idx}`}>
                              <button
                                type="button"
                                onClick={() => goToItem(cat, item)}
                                className="w-full text-left px-3 py-2.5 hover:bg-surface transition-colors flex items-start gap-2 group"
                                data-testid={`global-search-item-${meta.testid}-${idx}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-text-primary line-clamp-1">
                                    {getItemLabel(cat, item)}
                                  </p>
                                  {getItemSubtitle(cat, item) && (
                                    <p className="text-xs text-text-muted line-clamp-1 mt-0.5">
                                      {getItemSubtitle(cat, item)}
                                    </p>
                                  )}
                                </div>
                                <ArrowRight className="w-3.5 h-3.5 text-text-muted group-hover:text-primary flex-shrink-0 mt-1" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <button
                          type="button"
                          onClick={() => goToCategory(cat)}
                          className="w-full px-3 py-3 hover:bg-surface transition-colors flex items-center justify-between group"
                          data-testid={`global-search-see-all-${meta.testid}`}
                        >
                          <span className="text-sm text-text-primary">
                            Voir les {total} {meta.label.toLowerCase()}
                          </span>
                          <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* Footer: search all */}
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="w-full px-3 py-3 bg-primary/10 hover:bg-primary/20 transition-colors flex items-center justify-center gap-2 text-sm text-primary font-medium"
                  data-testid="global-search-see-all-results"
                >
                  Voir tous les résultats ({totalHits})
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GlobalSearchBar;
